<template>
  <div class="realEstatePanel">
    <div class="mt-2" v-if="loaded">
      <validation-observer ref="refFormObserver">

        <!-- Header -->
        <div class="d-flex">
          <feather-icon icon="SendIcon" size="19"/>
          <h4 class="mb-0 ml-50">{{ msg('New Message Template') }}</h4>
        </div>

        <!-- Form -->
        <b-row class="mt-1">

          <!-- ACTIVE -->
          <b-col cols="12" md="6" lg="4">
            <b-form-group :label="msg('Active')">
              <b-checkbox switch v-model="activeEvent">{{ msg('Active') }}</b-checkbox>
            </b-form-group>
          </b-col>

          <!-- ACTIVE -->
          <b-col cols="12" md="6" lg="4">
            <b-form-group :label="msg('Trigger Event')">
              <b-checkbox switch v-model="triggerEvent"></b-checkbox>
            </b-form-group>
          </b-col>

          <!-- NEW PROPERTY -->
          <b-col cols="12" md="6" lg="4">
            <b-form-group :label="msg('Add to all new properties')">
              <b-checkbox switch v-model="newProperty"/>
            </b-form-group>
          </b-col>


          <!-- ALIAS -->
          <b-col cols="12" md="6" lg="3">
            <b-form-group :label="msg('Alias')">
              <validation-provider #default="{ errors }" :name="msg('Alias')" rules="required">
                <b-form-input v-model="messageTemplate.alias"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- CHANNEL -->
          <b-col cols="12" md="6" lg="3">
            <b-form-group :label="msg('Channel')">
              <validation-provider #default="{ errors }" :name="msg('Channel')" rules="required">
                <v-select
                    :dir="isRTL ? 'rtl' : 'ltr'"
                    :clearable="false"
                    v-model="messageTemplate.messageChannel"
                    :options="allChannels"
                    class="w-100"
                    label="text"
                    :reduce="b => b.value"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <template v-if="triggerEvent !== true">
            <!-- EVENT -->
            <b-col cols="12" md="6" lg="3">
              <b-form-group :label="msg('Event')">
                <validation-provider #default="{ errors }" :name="msg('Event')" rules="required">
                  <v-select
                      :dir="isRTL ? 'rtl' : 'ltr'"
                      :clearable="false"
                      v-model="messageTemplate.messageEvent"
                      :options="allEvents"
                      class="w-100"
                      label="text"
                      :reduce="x => x.value"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- DURATION -->
            <b-col cols="12" md="6" lg="3">
              <b-form-group :label="msg('Duration in minutes')">
                <validation-provider #default="{ errors }" :name="msg('Duration')" rules="required">
                  <v-select
                      :dir="isRTL ? 'rtl' : 'ltr'"
                      :clearable="false"
                      :options="minuteOptions"
                      v-model="messageTemplate.minutes"
                      class="w-100"
                      label="label"
                      :reduce="x => x.value"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>


          </template>
          <template v-else>
            <!-- EVENT -->
            <b-col cols="12" md="6" lg="3">
              <b-form-group :label="msg('Trigger')">
                <validation-provider #default="{ errors }" :name="msg('Trigger')" rules="required">
                  <v-select
                      :dir="isRTL ? 'rtl' : 'ltr'"
                      :clearable="false"
                      v-model="messageTemplate.messageTrigger"
                      :options="allTriggers"
                      class="w-100"
                      label="text"
                      :reduce="x => x.value"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

          </template>

          <!-- REAL ESTATE PICKER (hidden when WHATSAPP) -->
          <b-col
              v-if="messageTemplate.messageChannel !== 'WHATSAPP'"
              cols="12" md="12" lg="12"
          >
            <b-form-group :label="msg('Real estate')">
              <validation-provider #default="{ errors }" :name="msg('Properties')" rules="required">
                <real-estate-picker v-model="messageTemplate.propertyIds" multiple/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

        </b-row>

        <!-- EMAIL SUBJECT -->
        <b-row class="mt-1">
          <b-col v-if="messageTemplate.messageChannel === 'EMAIL'" cols="12" md="6" lg="6">
            <b-form-group :label="msg('Subject (Email only)')">
              <b-form-input v-model="messageTemplate.subject"/>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- WHATSAPP MODE -->
        <b-row v-if="messageTemplate.messageChannel === 'WHATSAPP'" class="mt-2">

          <!-- BRANCH PICKER -->
          <b-col cols="12" md="6">
            <b-form-group :label="msg('Branch (WhatsApp sender)')">
              <v-select
                  :dir="isRTL ? 'rtl' : 'ltr'"
                  :clearable="false"
                  v-model="messageTemplate.branchId"
                  :options="allBranches"
                  class="w-100"
                  label="name"
                  :reduce="b => b.id"
                  @input="onBranchPicked"
              />
            </b-form-group>
          </b-col>

          <!-- TEMPLATES GRID -->
          <b-col v-if="branchTemplatesLoaded" cols="12" class="mt-1">
            <b-form-group label="Choose a WhatsApp Template">

              <!-- Loading -->
              <div v-if="loadingWhatsappTemplates" class="text-center py-4">
                <b-spinner small class="mr-2"></b-spinner>
                Loading templates…
              </div>

              <!-- Empty -->
              <div v-else-if="whatsappTemplates.length === 0" class="text-center py-4 text-muted">
                No templates found for this branch.
              </div>

              <!-- Grid -->
              <div v-else class="template-grid">

                <div
                    v-for="tpl in whatsappTemplates"
                    :key="tpl.id"
                    :class="[
                    'template-card',
                    { 'template-card-selected': selectedWhatsappTemplate && selectedWhatsappTemplate.id === tpl.id }
                  ]"
                    @click="selectWhatsappTemplate(tpl)"
                >

                  <div class="d-flex align-items-center justify-content-between">
                    <div class="font-weight-bold text-truncate">{{ tpl.name }}</div>

                    <b-badge :variant="tpl.status === 'APPROVED' ? 'success' : 'secondary'">
                      {{ tpl.status || 'UNKNOWN' }}
                    </b-badge>
                  </div>

                  <!-- META -->
                  <div class="mt-2 small d-flex align-items-center flex-wrap">
                    <span class="mr-2"><i class="fas fa-language mr-1"></i>{{ tpl.language }}</span>
                    <span class="mr-2"><i class="fas fa-folder-open mr-1"></i>{{ tpl.category }}</span>
                  </div>

                  <!-- BODY -->
                  <div class="template-body mt-3">
                    {{ getTemplateBody(tpl) }}
                  </div>

                  <div class="template-actions">
                    <b-button variant="outline-primary" size="sm" @click.stop="selectWhatsappTemplate(tpl)">
                      Use Template
                    </b-button>
                  </div>

                </div>
              </div>

            </b-form-group>
          </b-col>
        </b-row>

        <!-- NORMAL MESSAGE BODY (HIDDEN when WHATSAPP) -->
        <b-row v-if="messageTemplate.messageChannel !== 'WHATSAPP'" class="mt-2">
          <b-col cols="12">
            <b-form-group>
              <h5 style="color: dodgerblue">Placeholder Options</h5>

              <div class="text-center mb-1">
                <b-button
                    size="sm"
                    v-for="tag in textTags"
                    :key="tag.value"
                    pill
                    variant="outline-secondary"
                    class="mr-1"
                    @click="() => insertPlaceholder(tag.value)"
                >
                  {{ tag.label }}
                </b-button>
              </div>

              <validation-provider #default="{ errors }" :name="msg('Message body')">
                <RichTextEditor
                    v-model="messageTemplate.body"
                    ref="textEditor"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>

            </b-form-group>
          </b-col>
        </b-row>

        <!-- QUICK RESPONSES (HIDDEN when WHATSAPP) -->
        <b-row v-if="messageTemplate.messageChannel !== 'WHATSAPP'" class="mt-3">
          <b-col cols="12" class="mb-1"><h5>Quick Responses</h5></b-col>

          <b-col xs="6" sm="3" class="mb-1">
            <b-button variant="outline-primary" block @click="generatePhoneAndBookingText">
              {{ msg('Phone number and Bookingcode') }}
            </b-button>
          </b-col>

          <b-col xs="6" sm="3" class="mb-1">
            <b-button disabled variant="outline-primary" block>Custom</b-button>
          </b-col>

          <b-col xs="6" sm="3" class="mb-1">
            <b-button disabled variant="outline-primary" block>Custom</b-button>
          </b-col>

          <b-col xs="6" sm="3">
            <b-button disabled variant="outline-primary" block>Custom</b-button>
          </b-col>
        </b-row>

      </validation-observer>
    </div>

    <!-- ERROR -->
    <div v-else-if="error">
      <b-alert show variant="danger">
        <i class="fas fa-exclamation-triangle mr-3"></i>
        <strong>{{ error }}</strong>
      </b-alert>
    </div>

    <!-- LOADING -->
    <div v-else>
      <div style="min-height: 300px;text-align: center">
        <div style="margin-top: 20%">
          <b-spinner label="Spinning"/>
          {{ msg('loading') }}
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import RealEstatePicker from "@/components/components/picker/realEstatesPicker.vue";
import RichTextEditor from "@/components/widgets/RichTextEditor.vue";

const messageTemplateFilter = () => ({
  like: {},
  eq: {},
  lte: {},
  gte: {},
  in: {}
});

const buildingFilter = () => ({
  like: {},
  eq: {},
  lte: {},
  gte: {},
  in: {}
});

export default {
  name: "messageTemplatePanel",
  components: {RealEstatePicker,RichTextEditor },

  data() {
    return {
      loaded: false,
      error: null,

      activeEvent: true,
      triggerEvent: true,
      newProperty: false,

      buildingFilter: buildingFilter(),
      buildings: [],

      messageTemplate: {
        company: ""
      },

      // WhatsApp Logic
      whatsappTemplates: [],
      loadingWhatsappTemplates: false,
      selectedWhatsappTemplate: null,
      branchTemplatesLoaded: false,

      minuteOptions: [
        {label: "30 minutes", value: 30},
        {label: "1 Hour", value: 60},
        {label: "1.5 Hour", value: 90},
        {label: "2 Hours", value: 120},
        {label: "2.5 Hours", value: 150},
        {label: "3 Hours", value: 180},
        {label: "3.5 Hours", value: 210},
        {label: "4 Hours", value: 240},
        {label: "4.5 Hours", value: 270},
        {label: "5 Hours", value: 300}
      ],

      listeners: {
        submit: () => {
        }
      }
    };
  },

  props: {
    id: Number
  },

  computed: {
    ...mapGetters("data", ["getAllBranches"]),
    ...mapGetters("messageTemplate", ["allChannelsList", "allEventsList","allTriggersList"]),

    allChannels() {
      return Object.values(this.allChannelsList);
    },
    allEvents() {
      return Object.values(this.allEventsList);
    },
    allTriggers() {
      return Object.values(this.allTriggersList);
    },
    allBranches() {
      return Object.values(this.getAllBranches);
    },

    textTags() {
      return [
        {value: "#firstname#", label: "FirstName"},
        {value: "#lastname#", label: "Lastname"},
        {value: "#bookingCode#", label: "Booking code"},
        {value: "#fullAddressApartment#", label: "FullAddressApartment"},
        {value: "#wifiUserName#", label: "WifiUsername"},
        {value: "#wifiPassword#", label: "WifiPassword"},
        {value: "#keyBoxCode#", label: "KeyBoxCode"},
        {value: "#ownerMail#", label: "OwnerMail"},
        {value: "#ownerPhone#", label: "OwnerPhone"},
        {value: "#ownerCompany#", label: "OwnerCompany"}
      ];
    }
  },

  watch: {
    id() {
      this.onLoad();
      this.error = null;
    },
    "messageTemplate.messageChannel"(val) {
      if (val === "WHATSAPP") this.resetWhatsappState();
    }
  },

  created() {
    let $this = this;

    this.getAllBuildings({
      page: 1,
      amount: 9999,
      filter: this.buildingFilter,
      sortcolumn: "updatedDate",
      sortby: "desc"
    }).then(this.onBuildingsRetrieve, error => (this.error = error));

    this.onLoad();

    this.$root.$on(
        "crm::submitMessageTemplate",
        (this.listeners.submit = () => {
          this.submit();
        })
    );
  },

  beforeDestroy() {
    this.$root.$off("crm::submitMessageTemplate", this.listeners.submit);
  },

  methods: {
    ...mapActions("messageTemplate", {
      _register: "addMessageTemplate"
    }),

    ...mapActions("building", ["getAllBuildings"]),

    ...mapActions("whatsapp", ["getWhatsappTemplates", "getWhatsappTemplatesByBranch"]),

    /** RESET WHATSAPP WHEN CHANGED */
    resetWhatsappState() {
      this.messageTemplate.branchId = null;
      this.whatsappTemplates = [];
      this.selectedWhatsappTemplate = null;
      this.branchTemplatesLoaded = false;
    },

    /** WHEN BRANCH PICKED */
    async onBranchPicked(branchId) {
      if (!branchId) return;

      this.loadingWhatsappTemplates = true;
      this.branchTemplatesLoaded = false;

      try {
        const resp = await this.getWhatsappTemplatesByBranch({branchId});
        this.whatsappTemplates = resp.response || [];
      } catch (e) {
        console.error("Failed to load WA templates", e);
      }

      this.loadingWhatsappTemplates = false;
      this.branchTemplatesLoaded = true;
    },

    /** SELECT WA TEMPLATE */
    selectWhatsappTemplate(tpl) {
      this.selectedWhatsappTemplate = tpl;
      this.messageTemplate.body = tpl.name; // save id
      this.messageTemplate.subject = tpl.language; // save id
    },

    /** GET TEMPLATE BODY */
    getTemplateBody(tpl) {
      const body = tpl.components?.find(c => c.type === "BODY");
      return body?.text || "";
    },

    /** LOAD complete */
    onLoad() {
      this.loaded = true;
    },

   /* /!** QUILL EDITOR *!/
    onEditorChange({html}) {
      if (this.messageTemplate.body !== html) {
        this.messageTemplate.body = html;
      }
    },

    insertPlaceholder(placeholder) {
      const quill = this.$refs.quillEditor?.quill;
      if (!quill) return;

      const r = quill.getSelection(true);
      quill.insertText(r.index, placeholder);
      quill.setSelection(r.index + placeholder.length);
    },

    generatePhoneAndBookingText() {
      const quill = this.$refs.quillEditor.quill;
      const r = quill.getSelection(true);

      const msg = `Dear customer, your phone: #phone# | booking code: #bookingCode#.`;
      quill.insertText(r.index, msg);
      quill.setSelection(r.index + msg.length);
    },*/

    insertPlaceholder(placeholder) {
      // calls the method we exposed in RichTextEditor
      this.$refs.textEditor?.insertText(placeholder);
    },

    generatePhoneAndBookingText() {
      const msg = `Dear customer, your phone: #phone# | booking code: #bookingCode#.`;
      this.$refs.textEditor?.insertText(msg);
    },


    /** SUBMIT */
    submit() {
      return this.createMessageTemplate();
    },

    createMessageTemplate() {
      this.messageTemplate.active = this.activeEvent;
      this.messageTemplate.trigger = this.triggerEvent;
      this.messageTemplate.newProperty = this.newProperty;

      this.$refs.refFormObserver.validate().then(valid => {
        if (!valid) return;

        this._register(this.messageTemplate).then(
            id => {
              this.$emit("submitted", {status: true, id, server: id});
            },
            err => {
              this.$emit("submitted", {status: false, server: err});
            }
        );
      });
    },

    onBuildingsRetrieve(data) {
      this.buildings = data.Buildings;
    }
  }
};
</script>

<style scoped>
.dark-layout #realEstate-description {
  background-color: rgba(255, 255, 255, 0.66);
  color: #000;
}

.quill-container #realEstate-description {
  min-height: 400px;
}

/* WhatsApp Template Grid */
.template-grid {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 12px;
}

@media (min-width: 576px) {
  .template-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 992px) {
  .template-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.template-card {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: 0.15s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.template-card:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.template-card-selected {
  border: 2px solid #e75634 !important;
  box-shadow: 0 0 8px rgba(231, 86, 52, 0.4);
  transform: translateY(-2px);
}

.template-body {
  font-size: 0.95rem;
  line-height: 1.25rem;
  white-space: pre-wrap;
}

.template-actions {
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
}
</style>
